import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, AlertTriangle, Clock, RotateCcw, 
  XCircle, FileText, PlayCircle, X
} from 'lucide-react';
import { useGameSounds } from '../../../core/hooks/useGameSounds';
import ConfidenceSlider from '../../../components/shared/ConfidenceSlider';
import TacticalOverlay from '../../../components/shared/TacticalOverlay';

export default function LeaseDefender({ gameData, onComplete }) {
  const { playError, playWin, playExtinguish, playSuccess } = useGameSounds();
  
  const { 
    leaseText = [], 
    totalErrors = 0, 
    id,
    preGameBriefing,
    failureLogic
  } = gameData || {};

  const [stage, setStage] = useState('briefing'); 
  const [confidencePre, setConfidencePre] = useState(5);
  const [confidencePost, setConfidencePost] = useState(5);
  const [startTime, setStartTime] = useState(null);
  const [firstReactionTime, setFirstReactionTime] = useState(null);

  // Game Logic State
  const [shuffledLease, setShuffledLease] = useState([]);
  const [foundIds, setFoundIds] = useState([]);
  const [strikes, setStrikes] = useState(0);
  const [maxStrikes, setMaxStrikes] = useState(3);
  const [timeLeft, setTimeLeft] = useState(60);
  
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [activeIntervention, setActiveIntervention] = useState(null);

  // --- 1. DIFFICULTY CALCULATION ---
  const calculateDifficulty = (conf) => {
      // 8-10: Expert (30s, 1 Strike)
      if (conf >= 8) return { time: 30, strikes: 1 };
      // 5-7: Standard (60s, 3 Strikes)
      if (conf >= 5) return { time: 60, strikes: 3 };
      // 0-4: Rookie (90s, 5 Strikes)
      return { time: 90, strikes: 5 };
  };

  // --- 2. INITIALIZATION ---
  useEffect(() => {
    if (preGameBriefing) {
        setStage('briefing');
    } else {
        setStage('pre');
    }
    const shuffled = [...leaseText].sort(() => Math.random() - 0.5);
    setShuffledLease(shuffled);
  }, [preGameBriefing, leaseText]);

  // --- 3. AUTO-DISMISS FEEDBACK (The Fix) ---
  useEffect(() => {
    if (activeFeedback) {
      const timer = setTimeout(() => {
        setActiveFeedback(null);
      }, 4000); // 4 Seconds
      return () => clearTimeout(timer);
    }
  }, [activeFeedback]);

  // --- 4. START GAME HANDLER ---
  const handleStartGame = () => {
      const settings = calculateDifficulty(confidencePre);
      setTimeLeft(settings.time);
      setMaxStrikes(settings.strikes);
      setStrikes(0);
      setFoundIds([]);
      setStage('playing');
      setStartTime(Date.now());
  };

  // --- 5. TIMER ---
  useEffect(() => {
    if (stage !== 'playing') return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          triggerFailure('timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [stage]);

  if (!gameData) return <div className="p-10 text-center text-red-500">Error: Game Data Missing</div>;

  // --- 6. FAILURE LOGIC ROUTER ---
  const triggerFailure = (reasonOverride = null) => {
      setStage('intervention');
      playExtinguish();

      let reasonCode = reasonOverride || 'timeout';

      if (reasonCode === 'timeout') {
          const missedRepairs = !foundIds.includes('trap_repairs');
          const missedPrivacy = !foundIds.includes('trap_entry');
          if (missedRepairs) reasonCode = 'missed_liability';
          else if (missedPrivacy) reasonCode = 'missed_privacy';
      }

      const logic = failureLogic?.[reasonCode] || {
          title: "Contract Signed",
          script: "Time's up. You signed a lease with illegal clauses.",
          videoUrl: null
      };

      setActiveIntervention(logic);
  };

  const handleRetry = () => {
      setFoundIds([]);
      setStrikes(0);
      setActiveFeedback(null);
      setShuffledLease([...leaseText].sort(() => Math.random() - 0.5));
      
      const settings = calculateDifficulty(confidencePre);
      setTimeLeft(settings.time);
      
      setStage('playing');
      setActiveIntervention(null);
  };

  // --- 7. CLICK HANDLER (STRIKE SYSTEM) ---
  const handleSegmentClick = (segment) => {
    if (stage !== 'playing' || foundIds.includes(segment.id)) return;

    if (segment.isError) {
      // --- CORRECT HIT ---
      playSuccess();
      if (foundIds.length === 0) setFirstReactionTime(Date.now() - startTime);

      const newFound = [...foundIds, segment.id];
      setFoundIds(newFound);
      setActiveFeedback({ type: 'success', msg: segment.feedback });

      if (newFound.length === totalErrors) {
        setStage('post');
        playWin();
      }
    } else {
      // --- WRONG HIT (STRIKE) ---
      playError();
      const newStrikes = strikes + 1;
      setStrikes(newStrikes);
      
      const strikesLeft = maxStrikes - newStrikes;

      if (strikesLeft <= 0) {
          triggerFailure('voided_contract');
      } else {
          setActiveFeedback({ type: 'error', msg: `Legal Clause Flagged! ${strikesLeft} strikes remaining.` });
          setTimeLeft(prev => Math.max(0, prev - 5)); 
      }
    }
  };

  const handleFinalLog = () => {
    const masteryPayload = {
      moduleId: id,
      confidencePre,
      confidencePost,
      confidenceDelta: confidencePost - confidencePre,
      reactionTime: firstReactionTime,
      detectionRate: (foundIds.length / totalErrors) * 100,
      timestamp: Date.now()
    };
    onComplete(masteryPayload);
  };

  return (
    <div className="h-full min-h-[500px] flex flex-col bg-slate-50 rounded-2xl overflow-hidden border relative shadow-inner">
      <AnimatePresence mode="wait">
        
        {/* --- OVERLAY: BRIEFING --- */}
        {stage === 'briefing' && preGameBriefing && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay 
                    type="briefing" 
                    data={preGameBriefing} 
                    onContinue={() => setStage('pre')} 
                />
            </div>
        )}

        {/* --- OVERLAY: INTERVENTION --- */}
        {stage === 'intervention' && activeIntervention && (
            <div className="absolute inset-0 z-[100]">
                <TacticalOverlay 
                    type="intervention" 
                    data={activeIntervention} 
                    onRetry={handleRetry} 
                />
            </div>
        )}

        {/* STAGE 1: BASELINE (Sets Difficulty) */}
        {stage === 'pre' && (
          <motion.div 
            key="pre" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="flex-grow flex items-center justify-center p-6 bg-white z-50"
          >
            <div className="max-w-md w-full">
              <ConfidenceSlider 
                value={confidencePre} onChange={setConfidencePre}
                title="Baseline Assessment"
                subtitle="How confident are you in identifying illegal clauses in a rental lease?"
              />
              <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center border border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Sim Difficulty: {confidencePre >= 8 ? "EXPERT" : confidencePre >= 5 ? "STANDARD" : "TRAINING"}</p>
                  <div className="flex justify-center gap-4 text-sm font-medium text-gray-700">
                      <span className="flex items-center gap-1"><Clock size={16}/> {calculateDifficulty(confidencePre).time}s</span>
                      <span className="flex items-center gap-1"><XCircle size={16}/> {calculateDifficulty(confidencePre).strikes} Strikes</span>
                  </div>
              </div>
              <button onClick={handleStartGame} className="mt-6 w-full bg-blue-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-blue-700 transition">
                <PlayCircle /> START RED-LINE AUDIT
              </button>
            </div>
          </motion.div>
        )}

        {/* STAGE 2: LEASE AUDIT */}
        {stage === 'playing' && (
          <motion.div key="playing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col h-full bg-slate-100 absolute inset-0">
            <div className="p-4 border-b bg-white flex justify-between items-center shadow-sm z-10">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Lease Audit</span>
                <div className="flex gap-1">
                  {[...Array(totalErrors)].map((_, i) => (
                    <div key={i} className={`w-3 h-3 rounded-full border ${i < foundIds.length ? 'bg-red-500 border-red-600' : 'bg-gray-200 border-gray-300'}`} />
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                  {/* STRIKE COUNTER */}
                  <div className="flex items-center gap-1">
                      {[...Array(maxStrikes)].map((_, i) => (
                          <XCircle key={i} size={16} className={`${i < strikes ? 'text-red-600' : 'text-gray-200'}`} />
                      ))}
                  </div>
                  <div className="flex items-center font-mono font-bold text-gray-600">
                    <Clock size={16} className={`mr-2 ${timeLeft < 10 ? 'text-red-500 animate-pulse' : ''}`} />
                    00:{timeLeft.toString().padStart(2, '0')}
                  </div>
              </div>
            </div>

            <div className="flex-grow p-6 overflow-y-auto">
              <div className="max-w-2xl mx-auto bg-white shadow-xl p-10 border-t-8 border-blue-600 min-h-full relative">
                
                <div className="flex justify-between items-start mb-8 pb-4 border-b">
                   <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2"><FileText /> Standard Residential Lease</h1>
                   <div className="text-[10px] font-bold text-gray-400 text-right uppercase">Confidential Document<br/>Ref: NJ-RES-2025</div>
                </div>
                
                <div className="space-y-6 text-lg text-gray-800 leading-relaxed font-serif">
                  {shuffledLease.map((segment) => (
                    <p 
                      key={segment.id}
                      onClick={() => handleSegmentClick(segment)}
                      className={`cursor-crosshair transition-all rounded p-1 hover:bg-yellow-50 active:bg-yellow-100 
                        ${foundIds.includes(segment.id) ? 'line-through decoration-red-600 decoration-4 bg-red-50 text-red-900 opacity-60' : ''}
                      `}
                    >
                      {segment.text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* STAGE 3: GROWTH CONFIRMATION */}
        {stage === 'post' && (
          <motion.div key="post" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-white/90 backdrop-blur-sm">
            <div className="max-w-md w-full">
              <ConfidenceSlider 
                value={confidencePost} onChange={setConfidencePost}
                title="Growth Confirmation"
                subtitle="How confident are you now in your ability to defend your renter rights?"
              />
              <button onClick={() => setStage('results')} className="mt-8 w-full bg-green-600 text-white font-black py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 hover:bg-green-700 transition">
                VIEW READINESS REPORT
              </button>
            </div>
          </motion.div>
        )}

        {/* STAGE 4: RESULTS */}
        {stage === 'results' && (
          <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 bg-white z-[60] flex flex-col items-center justify-center p-8 text-center">
            <ShieldCheck size={80} className="text-green-500 mb-4" />
            <h2 className="text-3xl font-black text-gray-900 mb-2 uppercase">Renter Rights Secured</h2>
            <p className="text-gray-600 mb-8 max-w-sm">You identified every predatory clause. You are officially high-risk for bad landlords.</p>
            
            <div className="grid grid-cols-2 gap-4 w-full max-w-sm mb-10">
                <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Confidence Delta</p>
                <p className="text-2xl font-black text-blue-600">+{confidencePost - confidencePre}</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100">
                <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Instinct Speed</p>
                <p className="text-2xl font-black text-indigo-600">{(firstReactionTime / 1000).toFixed(2)}s</p>
                </div>
            </div>

            <button onClick={handleFinalLog} className="bg-gray-900 text-white font-black py-4 px-10 rounded-2xl shadow-lg hover:bg-black transition">
                LOG LEGAL LITERACY SIGNAL
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeFeedback && (
          // FIX: Changed from 'bottom-6' to 'top-20' to avoid blocking footer buttons
          <motion.div 
            initial={{ y: -50, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            exit={{ y: -50, opacity: 0 }} 
            className="absolute top-20 left-6 right-6 z-[70]"
          >
            <div className={`${activeFeedback.type === 'error' ? 'bg-red-600 border-red-800' : 'bg-indigo-600 border-indigo-800'} text-white p-5 rounded-2xl shadow-2xl flex items-start gap-4 border-b-4 cursor-pointer`} onClick={() => setActiveFeedback(null)}>
              {activeFeedback.type === 'error' ? <XCircle className="w-8 h-8 flex-shrink-0 text-red-200" /> : <ShieldCheck className="w-8 h-8 flex-shrink-0 text-indigo-200" />}
              <div>
                <p className={`font-black text-xs uppercase tracking-tighter mb-1 ${activeFeedback.type === 'error' ? 'text-red-100' : 'text-indigo-100'}`}>
                    {activeFeedback.type === 'error' ? 'Mistake Budget Warning' : 'Shield Up: State Law Activated'}
                </p>
                <p className="text-sm font-medium leading-snug">{activeFeedback.msg}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}